import { Maybe, maybe, rd, RemoteData } from '@passionware/monads';
import { promiseState } from '@passionware/platform-react';
import { createSimpleStore } from '@passionware/simple-store';
import { useMemo } from 'react';
import { MountElementProps } from 'v5/messages/mount-element';
import { AtellioFeatureGroupDefinition } from 'v5/features';
import { FeatureFlagValues } from 'v5/platform/feature-management/feature-management';
import { ReactNode } from 'react';
import {
  CustomFieldAddDialog,
  CustomFieldCatalogItem
} from './CustomFieldAddDialog';
import { FieldDefinition } from './field-definition';

interface CustomFieldAddDialogWidgetProps extends MountElementProps {
  features: FeatureFlagValues<AtellioFeatureGroupDefinition>;
  prompt: ReactNode;
  catalog: CustomFieldCatalogItem[];
  initialAssignedOrder: readonly number[];
  onCreate: (field: FieldDefinition) => Promise<void>;
  onSaveFields: (assignedIds: number[]) => Promise<void>;
}

export function CustomFieldAddDialogWidget(
  props: CustomFieldAddDialogWidgetProps
) {
  const initialFieldStore = useMemo(
    () =>
      createSimpleStore<RemoteData<Maybe<FieldDefinition>>>(
        rd.of<Maybe<FieldDefinition>>(null)
      ),
    []
  );
  const mutation = promiseState.useMutation(
    async (
      payload:
        | { type: 'create'; field: FieldDefinition }
        | { type: 'saveFields'; assignedIds: number[] }
    ) => {
      if (payload.type === 'create') {
        await props.onCreate(payload.field);
        return;
      }
      await props.onSaveFields(payload.assignedIds);
    }
  );

  const handleCreate = async (field: Maybe<FieldDefinition>) => {
    await mutation.track({
      type: 'create',
      field: maybe.getOrThrow(field)
    });
    props.close();
  };

  const handleSaveFields = async (assignedIds: ReadonlySet<number>) => {
    await mutation.track({
      type: 'saveFields',
      assignedIds: [...assignedIds]
    });
    props.close();
  };

  return (
    <CustomFieldAddDialog
      open={props.open}
      features={props.features}
      onSubmit={handleCreate}
      mutation={mutation.state}
      prompt={props.prompt}
      onCancel={props.close}
      resetStore={initialFieldStore}
      catalog={props.catalog}
      initialAssignedOrder={props.initialAssignedOrder}
      onSaveFields={handleSaveFields}
    />
  );
}
